import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { subDays } from "date-fns";
import { RootState } from "../../../common/state/store";

export interface DateRange {
  startDate: string | null; // ISO string for Redux serialization
  endDate: string | null;
}

export interface StatsNewState {
  dateRange: DateRange;
  compareDateRange: DateRange | null;
  isCompareEnabled: boolean;
  searchValue: string;
  sortKey: string;
  activeTab: "global" | "parcours" | "apprenants";
  selectedParcoursId: number | null;
  selectedModuleId: number | null;
  selectedApprenantId: number | null;
  selectedSessionId: number | null;
  // Cache metadata
  cachedForOrgId: number | null;
  lastFetchTimestamp: number | null;
  cachedModulesForParcoursId: number | null;
}

const initialState: StatsNewState = {
  dateRange: {
    startDate: subDays(new Date(), 29).toISOString(),
    endDate: new Date().toISOString(),
  },
  compareDateRange: null,
  isCompareEnabled: false,
  searchValue: "",
  sortKey: "recent",
  activeTab: "global",
  selectedParcoursId: null,
  selectedModuleId: null,
  selectedApprenantId: null,
  selectedSessionId: null,
  // Cache metadata
  cachedForOrgId: null,
  lastFetchTimestamp: null,
  cachedModulesForParcoursId: null,
};

export const statsNewSlice = createSlice({
  name: "statsNew",
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload;
    },
    setCompareDateRange: (state, action: PayloadAction<DateRange | null>) => {
      state.compareDateRange = action.payload;
    },
    setIsCompareEnabled: (state, action: PayloadAction<boolean>) => {
      state.isCompareEnabled = action.payload;
      if (!action.payload) {
        state.compareDateRange = null;
      }
    },
    setSearchValue: (state, action: PayloadAction<string>) => {
      state.searchValue = action.payload;
    },
    setSortKey: (state, action: PayloadAction<string>) => {
      state.sortKey = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<"global" | "parcours" | "apprenants">) => {
      state.activeTab = action.payload;
    },
    setSelectedParcoursId: (state, action: PayloadAction<number | null>) => {
      state.selectedParcoursId = action.payload;
    },
    setSelectedModuleId: (state, action: PayloadAction<number | null>) => {
      state.selectedModuleId = action.payload;
    },
    setSelectedApprenantId: (state, action: PayloadAction<number | null>) => {
      state.selectedApprenantId = action.payload;
    },
    setSelectedSessionId: (state, action: PayloadAction<number | null>) => {
      state.selectedSessionId = action.payload;
    },
    resetFilters: (state) => {
      state.searchValue = "";
      state.sortKey = "date-desc";
    },
    resetNavigation: (state) => {
      state.selectedParcoursId = null;
      state.selectedModuleId = null;
      state.selectedApprenantId = null;
      state.selectedSessionId = null;
    },
    // Cache management
    setCachedOrgId: (state, action: PayloadAction<number>) => {
      state.cachedForOrgId = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    setCachedModulesParcoursId: (state, action: PayloadAction<number>) => {
      state.cachedModulesForParcoursId = action.payload;
    },
    invalidateCreatorStatsCache: (state) => {
      state.cachedForOrgId = null;
      state.lastFetchTimestamp = null;
      state.cachedModulesForParcoursId = null;
    },
    invalidateCreatorModulesCache: (state) => {
      state.cachedModulesForParcoursId = null;
    },
  },
});

// Actions
export const {
  setDateRange,
  setCompareDateRange,
  setIsCompareEnabled,
  setSearchValue,
  setSortKey,
  setActiveTab,
  setSelectedParcoursId,
  setSelectedModuleId,
  setSelectedApprenantId,
  setSelectedSessionId,
  resetFilters,
  resetNavigation,
  setCachedOrgId,
  setCachedModulesParcoursId,
  invalidateCreatorStatsCache,
  invalidateCreatorModulesCache,
} = statsNewSlice.actions;

// Selectors
export const selectDateRange = (state: RootState) => state.statsNew.dateRange;
export const selectCompareDateRange = (state: RootState) => state.statsNew.compareDateRange;
export const selectIsCompareEnabled = (state: RootState) => state.statsNew.isCompareEnabled;
export const selectSearchValue = (state: RootState) => state.statsNew.searchValue;
export const selectSortKey = (state: RootState) => state.statsNew.sortKey;
export const selectActiveTab = (state: RootState) => state.statsNew.activeTab;
export const selectSelectedParcoursId = (state: RootState) => state.statsNew.selectedParcoursId;
export const selectSelectedModuleId = (state: RootState) => state.statsNew.selectedModuleId;
export const selectSelectedApprenantId = (state: RootState) => state.statsNew.selectedApprenantId;
export const selectSelectedSessionId = (state: RootState) => state.statsNew.selectedSessionId;
export const selectCachedForOrgId = (state: RootState) => state.statsNew.cachedForOrgId;
export const selectCachedModulesForParcoursId = (state: RootState) =>
  state.statsNew.cachedModulesForParcoursId;

// Helper to convert ISO strings back to Date objects (memoized to prevent unnecessary re-renders)
export const selectDateRangeAsDates = createSelector([selectDateRange], (dateRange) => ({
  startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
  endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
}));

export const selectCompareDateRangeAsDates = createSelector(
  [selectCompareDateRange],
  (compareDateRange) => {
    if (!compareDateRange) return null;
    return {
      startDate: compareDateRange.startDate ? new Date(compareDateRange.startDate) : null,
      endDate: compareDateRange.endDate ? new Date(compareDateRange.endDate) : null,
    };
  },
);

export const statsNewReducer = statsNewSlice.reducer;
